import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { forkJoin } from 'rxjs';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { HR_EMPLOYEE_STATUSES, HrDepartment, HrEmployee, HrEmployeeFilters, HrPosition } from '../hr.models';
import { HrService } from '../hr.service';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

@Component({
  selector: 'app-hr-employee-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent,
    HrModuleNavComponent
  ],
  templateUrl: './hr-employee-list.component.html',
  styleUrls: ['./hr-employee-list.component.scss']
})
export class HrEmployeeListComponent implements OnInit {
  private readonly hrService = inject(HrService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  employees: HrEmployee[] = [];
  departments: HrDepartment[] = [];
  positions: HrPosition[] = [];
  totalRecords = 0;
  filters: HrEmployeeFilters = { page: 0, pageSize: 50 };

  categoryOptions: { label: string; value: string }[] = [];
  readonly statusOptions = HR_EMPLOYEE_STATUSES.map(value => ({ label: this.format(value), value }));
  readonly linkOptions = [
    { label: 'Linked', value: true },
    { label: 'Missing', value: false }
  ];
  readonly canCreateEmployee = this.permissionService.hasPermission(app_permissions.hr.employees.create);
  readonly canEditEmployee = this.permissionService.hasPermission(app_permissions.hr.employees.edit);

  ngOnInit(): void {
    forkJoin({
      departments: this.hrService.departments(),
      positions: this.hrService.positions(),
      workerCategories: this.hrService.workerCategories()
    }).subscribe({
      next: response => {
        this.departments = response.departments.data ?? [];
        this.positions = response.positions.data ?? [];
        this.categoryOptions = (response.workerCategories.data ?? []).map(item => ({
          label: item.categoryName || this.format(item.categoryCode), value: item.categoryCode || ''
        }));
      }
    });
    this.load();
  }

  get firstRow(): number {
    return (this.filters.page || 0) * (this.filters.pageSize || 50);
  }

  get departmentOptions() {
    return this.departments.map(item => ({ label: item.departmentName || `Department ${item.departmentId}`, value: item.departmentId }));
  }

  get positionOptions() {
    return this.positions.map(item => ({ label: item.positionName || `Position ${item.positionId}`, value: item.positionId }));
  }

  load(): void {
    this.filters.page = this.filters.page || 0;
    this.filters.pageSize = this.filters.pageSize || 50;
    this.loading = true;
    this.hrService.employees(this.filters).subscribe({
      next: response => {
        this.employees = response.items ?? [];
        this.totalRecords = response.count ?? this.employees.length;
      },
      error: () => {
        this.employees = [];
        this.totalRecords = 0;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  onPage(event: { first?: number; rows?: number }): void {
    const rows = event.rows || this.filters.pageSize || 50;
    this.filters.pageSize = rows;
    this.filters.page = Math.floor((event.first || 0) / rows);
    this.load();
  }

  clearFilters(): void {
    this.filters = { page: 0, pageSize: this.filters.pageSize || 50 };
    this.load();
  }

  format(value?: string): string {
    return (value || '-').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'ACTIVE') return 'success';
    if (status === 'ONBOARDING') return 'warn';
    if (status === 'SUSPENDED' || status === 'TERMINATED') return 'danger';
    if (status === 'DRAFT') return 'info';
    return 'secondary';
  }
}
